import type { HeartRateEvent } from '@model/DeviceConfiguration';
import {
  eventToRelativeSecond,
  getSessionTrackOriginTimestamp,
} from '../../../shared/hr-data';
import type { ChartViewId, CustomTimeSegment } from '../types';
import { filterEventsByCustomSegment } from './customSegment';
import { buildSessionValueMap } from './sessionTrack';
import { getTrendSignalValue } from './featureValue';

export const getSessionAvailableSeconds = (
  events: HeartRateEvent[]
): number[] => {
  const valueMap = buildSessionValueMap(events, event =>
    getTrendSignalValue(event, 'HR')
  );
  return [...valueMap.keys()].sort((left, right) => left - right);
};

export const getWorkspaceAvailableSeconds = (
  sessions: Array<{ events?: HeartRateEvent[] }>
): number[] => {
  const seconds = new Set<number>();

  sessions.forEach(session => {
    getSessionAvailableSeconds(session.events ?? []).forEach(second =>
      seconds.add(second)
    );
  });

  return [...seconds].sort((left, right) => left - right);
};

export const resolveSegmentBounds = ({
  activeViewId,
  customSegments,
  availableSeconds,
  trendStart,
  trendEnd,
}: {
  activeViewId: ChartViewId;
  customSegments: CustomTimeSegment[];
  availableSeconds: number[];
  trendStart: string | null;
  trendEnd: string | null;
}): { startSecond: number; endSecond: number } | null => {
  if (availableSeconds.length === 0) return null;

  const minSecond = availableSeconds[0];
  const maxSecond = availableSeconds[availableSeconds.length - 1];

  if (activeViewId !== 'full') {
    const segment = customSegments.find(item => item.id === activeViewId);
    if (!segment) return null;
    return {
      startSecond: segment.startSecond,
      endSecond: segment.endSecond,
    };
  }

  const startSecond = trendStart ? Number(trendStart) : minSecond;
  const endSecond = trendEnd ? Number(trendEnd) : maxSecond;

  if (!Number.isFinite(startSecond) || !Number.isFinite(endSecond)) {
    return null;
  }

  if (startSecond >= endSecond) return null;

  return { startSecond, endSecond };
};

export const filterSessionEventsForSegment = (
  events: HeartRateEvent[],
  startSecond: number,
  endSecond: number
): HeartRateEvent[] =>
  filterEventsByCustomSegment(events, startSecond, endSecond);

export const getSessionTrackDurationSeconds = (
  events: HeartRateEvent[]
): number => {
  if (events.length === 0) return 0;

  const sorted = [...events].sort(
    (left, right) => left.timestamp - right.timestamp
  );
  const originTimestamp = getSessionTrackOriginTimestamp(sorted);
  if (originTimestamp == null) return 0;

  const lastSecond = eventToRelativeSecond(
    sorted[sorted.length - 1].timestamp,
    originTimestamp
  );

  return Math.max(0, lastSecond);
};
